import { changePercent, changeText, type PriceDirection } from './priceFormat';

function Row({ label, value, tone }: { label: string; value: string; tone?: 'up' | 'down' }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-line/40 px-2 py-1 odd:bg-panel2">
      <span className="flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className={tone === 'up' ? 'text-up' : tone === 'down' ? 'text-down' : 'text-muted'}
        >
          {tone === 'up' ? '▲' : tone === 'down' ? '▼' : '•'}
        </span>
        {label}
      </span>
      <span className="num font-semibold">{value}</span>
    </div>
  );
}

/**
 * Market Watch's "Details" view: everything about one symbol (bid/ask with their daily high and low, the daily change, the
 * day's open and the previous close), one row per figure, as MT5 shows it. The caller supplies the live quote and the bars.
 */
export function SymbolDetailsView({
  symbol,
  description,
  digits,
  quote,
  day,
  prevClose,
}: {
  symbol: string;
  description: string;
  digits: number;
  quote: { bid: number; ask: number; direction: PriceDirection } | undefined;
  /** Today's daily bar, or null when there is none yet. */
  day: { o: number; h: number; l: number } | null;
  /** Yesterday's close, or null. */
  prevClose: number | null;
}) {
  const fmt = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toFixed(digits));
  const spread = quote ? quote.ask - quote.bid : 0;
  // The daily bar is read now and then; the live bid can already be beyond it (same rule as the Symbols list).
  const bidHigh = day ? Math.max(day.h, quote?.bid ?? day.h) : null;
  const bidLow = day ? Math.min(day.l, quote?.bid ?? day.l) : null;
  const askHigh = bidHigh !== null && quote ? bidHigh + spread : null;
  const askLow = bidLow !== null && quote ? bidLow + spread : null;
  const change = quote ? changePercent(quote.bid, day?.o) : null;
  const tone = quote?.direction === 'flat' ? undefined : quote?.direction;

  return (
    <section className="h-full overflow-auto bg-panel text-sm" aria-label="Symbol details">
      <h3 className="px-2 py-1.5 font-semibold text-accent" data-testid="details-symbol">
        {symbol}
        {description ? `, ${description}` : ''}
      </h3>
      <div role="table" aria-label={`${symbol} figures`}>
        <Row label="Bid" value={fmt(quote?.bid)} tone={tone} />
        <Row label="Bid High" value={fmt(bidHigh)} />
        <Row label="Bid Low" value={fmt(bidLow)} />
        <Row label="Ask" value={fmt(quote?.ask)} tone={tone} />
        <Row label="Ask High" value={fmt(askHigh)} />
        <Row label="Ask Low" value={fmt(askLow)} />
        <Row
          label="Daily Change"
          value={changeText(change)}
          tone={change === null || change === 0 ? undefined : change > 0 ? 'up' : 'down'}
        />
        <Row label="Open Price" value={fmt(day?.o)} />
        <Row label="Close Price" value={fmt(prevClose)} />
      </div>
    </section>
  );
}
